import React from 'react';
import { Column } from 'react-table';
import SessionTable, {
  ResponseGuard,
  SessionTableInstanceProps
} from '../index';
import {
  MappingCell,
  DirectCell,
  YesNoCell,
  CellFunction,
  NAGuard,
  RESPONSE_NONE,
  RESPONSE_NA
} from '../../cells';
import { Capitalize } from '../../../../../libs/format';
import { IEpxGeneric, SessionType } from '../../../../../libs/apps/epxgeneric';

type Dependencies = [boolean, boolean, boolean];

const getDependencies = (sessions: IEpxGeneric[]): Dependencies => {
  let hasIssues = false;
  let hasWorseActivityLevel = false;
  let hasProgramComment = false;
  for (const session of sessions) {
    if (session.hasIssues) {
      hasIssues = true;
    }
    if (session.hasWorseActivityLevel) {
      hasWorseActivityLevel = true;
    }
    if (
      typeof session.programComment === 'string' &&
      session.programComment.trim().length > 0
    ) {
      hasProgramComment = true;
    }
  }
  return [hasIssues, hasWorseActivityLevel, hasProgramComment];
};

const MedicationCell: CellFunction<IEpxGeneric> = ({
  row: { original: session }
}) => {
  let output;
  if (session.isTakingMeds === true) {
    output = 'Yes';
  } else if (session.isTakingMeds === false) {
    output = 'No';
  }
  if (
    session.isTakingMeds == null &&
    (session.type == 'm' || session.type == 'esrd-m')
  ) {
    return RESPONSE_NONE;
  }
  if (!output) {
    output = RESPONSE_NA;
  }
  return <span>{output}</span>;
};

const IssuesCell: CellFunction<IEpxGeneric> = ({
  row: { original: session }
}) => {
  let output;
  if (session.hasIssues === true) {
    output = 'Yes';
  } else if (session.hasIssues === false) {
    output = 'No';
  } else if (!output) {
    output = RESPONSE_NA;
  }
  return <span>{output}</span>;
};

const SymptomsCell: CellFunction<IEpxGeneric> = ({
  row: { original: session }
}) => {
  let output;
  const emptyOutput = 'No Symptoms';
  function isSymptom(type: SessionType): boolean {
    if (type == SessionType.Symptom || type == SessionType.ESRDSymptom) {
      return true;
    }
    return false;
  }
  if (session.symptoms?.length > 0) {
    const values = session.symptoms;
    if (values == null) {
      return RESPONSE_NONE;
    }
    output = values
      .map((value) => {
        value = Capitalize(value);
        return value;
      })
      .join(', ');
    if (session.symptoms?.length == 0) {
      output = emptyOutput;
    }
  } else if (isSymptom(session.type) == true) {
    output = emptyOutput;
  } else {
    output = RESPONSE_NA;
  }
  return <span>{output}</span>;
};

const GetNACheck = (type: SessionType) => {
  return (session: IEpxGeneric): boolean => {
    return session.type !== type;
  };
};

const getColumns = (sessions: IEpxGeneric[]): Column<IEpxGeneric>[] => {
  const [hasIssues, hasWorseActivityLevel, hasProgramComment] =
    getDependencies(sessions);
  const columns: Column<IEpxGeneric>[] = [];
  columns.push({
    Header: 'Type',
    accessor: 'type',
    Cell: MappingCell('type', {
      [SessionType.Regular]: 'Regular',
      [SessionType.Symptom]: 'Symptoms',
      [SessionType.Medication]: 'Medication',
      [SessionType.Activity]: 'Activity Level',
      [SessionType.ESRD]: 'Regular',
      [SessionType.ESRDSymptom]: 'Symptoms',
      [SessionType.ESRDMed]: 'Medication',
      [SessionType.Binary]: 'Regular',
      [SessionType.BinaryActivity]: 'Activity Level'
    })
  });
  if (hasIssues) {
    columns.push({
      Header: 'Has Issues',
      accessor: 'hasIssues',
      Cell: IssuesCell
    });
  }
  columns.push(
    {
      Header: 'Symptoms',
      accessor: 'symptoms',
      Cell: SymptomsCell
    },
    {
      Header: 'Taking Meds',
      id: 'medication',
      Cell: MedicationCell
    }
  );
  if (hasWorseActivityLevel) {
    columns.push({
      Header: 'Has Worse Activity',
      id: 'hasWorseActivityLevel',
      Cell: NAGuard(
        ResponseGuard(
          YesNoCell('hasWorseActivityLevel') as CellFunction<IEpxGeneric>
        ),
        GetNACheck(SessionType.BinaryActivity)
      )
    });
  }
  columns.push({
    Header: 'Activity Level',
    accessor: 'activityLevel',
    Cell: NAGuard(
      ResponseGuard(DirectCell('activityLevel') as CellFunction<IEpxGeneric>),
      GetNACheck(SessionType.Activity)
    )
  });
  if (hasProgramComment) {
    columns.push({
      Header: 'Program Comment',
      accessor: 'programComment',
      Cell: ResponseGuard(
        DirectCell('programComment') as CellFunction<IEpxGeneric>
      )
    });
  }
  return columns;
};

const EpxGenericTable: React.FC<SessionTableInstanceProps> = (
  props
): JSX.Element => {
  return (
    <SessionTable<IEpxGeneric>
      url="/epxgeneric/data/"
      getColumns={getColumns}
      getDependencies={getDependencies}
      {...props}
    />
  );
};

export default EpxGenericTable;
